import { useContext, useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { BTN_PRIMARY_SM, BTN_SECONDARY_SM } from '../buttonStyles';
import { useAuth } from '../../contexts/AuthContext';
import { useViewerZone } from '../../hooks/useViewerZone';
import { useLessonClock } from '../../hooks/useLessonClock';
import { PairBookingsContext } from '../../contexts/PairBookingsContext';
import { subscribeToBooking } from '../../services/liveCallBookings';
import { lessonOver } from '../../utils/callEnd';
import { whenLine, zoneName } from '../../utils/liveCalendar';
import { liveLessonTitle } from '../../utils/liveLessonText';
import { lessonEventAction, lessonEventLine } from '../../utils/lessonEvent';
import { lessonsTarget } from '../../utils/openInLessons';
import type { Message } from '../../types/chat';
import type { LiveCallBooking } from '../../types/liveCall';
import { matchThumb } from '../../utils/matchThumb';

/**
 * A lesson event in the thread (decision 126; booking-contracts.md 11): booked, moved, a time changed, the
 * match uploaded, finished. One compact card: the line, the lesson's title, its date and time (the booking's,
 * so it stays right after another move), the match's first frame when there is one, and one action: the
 * player's Open; the coach's Prep before the call, Join while it is open, Open otherwise. `children` sits
 * under it (the coach's "Doesn't work? Suggest new times" on a move).
 */
export function LessonEventCard({ message, children }: { message: Message; children?: ReactNode }) {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const tz = useViewerZone();
  // The thread's lessons (one listener for the pair); a card reads its own only for a lesson outside them.
  const pair = useContext(PairBookingsContext);
  const fromPair = pair?.bookings.find((x) => x.id === message.bookingId);
  const alone = !pair || (pair.ready && !fromPair);
  const [own, setOwn] = useState<LiveCallBooking | null | undefined>(undefined);
  useEffect(() => (alone ? subscribeToBooking(message.bookingId!, setOwn) : undefined), [alone, message.bookingId]);
  const b = alone ? own : fromPair;
  // A live clock (audit 2 Nov, B14): Prep turns to Join as the call opens, and Join to Open when it ends.
  const now = useLessonClock(b ? [b] : []);
  const isCoach = !!b && b.coachId === currentUser?.uid;
  const title = message.lessonTitle ?? (b ? liveLessonTitle(b) : '');
  const action = b ? lessonEventAction({ isCoach, now, startAt: b.scheduledStartAt, windowOpenAt: b.windowOpenAt,
    // A miss held for the player (95) is done with on the coach's side: Open, as its row has no Join (the walk of 2 Nov).
    over: b.status !== 'confirmed' || lessonOver(b, now) || b.missResolution?.status === 'pending', matchReview: b.lessonMode === 'match_review', hasMatch: !!b.playerVideoUrl }) : null;
  const open = () => {
    if (!b) return;
    if (action === 'Join') { navigate(`/call/${b.id}`); return; }
    if (window.matchMedia('(max-width: 767px)').matches) { navigate(`/live/${b.id}`); return; }
    const t = lessonsTarget({ isCoach, bookingId: b.id, delivered: action === 'Open' && (b.status !== 'confirmed' || lessonOver(b, now)) });
    navigate(t.to, { state: t.state });
  };
  return (
    <div className="my-3 flex justify-center" data-lesson-event={message.liveEvent} data-lesson-booking={message.bookingId}>
      <div className="w-full max-w-[320px] rounded-[12px] border border-[#e6e6e6] bg-white px-4 py-3">
        {/* One line (2 Nov): what happened, the title, the time in the reader's zone, named; not the server's sentence. */}
        <div className="flex items-center gap-3">
          {b?.playerVideoUrl && (
            matchThumb(b) ? (
              // The server's first frame, kept on disk by the worker (speed.md 4); the video's own frame until it exists.
              <img src={matchThumb(b)!} alt="" aria-hidden="true" className="h-11 w-11 flex-shrink-0 rounded-[8px] bg-black object-cover" data-event-thumb />
            ) : (
              <video src={`${b.playerVideoUrl}#t=0.5`} preload="metadata" muted playsInline aria-hidden="true"
                className="h-11 w-11 flex-shrink-0 rounded-[8px] bg-black object-cover" data-event-thumb />
            )
          )}
          <p className="min-w-0 flex-1 [overflow-wrap:anywhere] text-[15px] leading-[20px] text-db-text-1" data-event-line>
            {b ? lessonEventLine({ event: message.liveEvent, text: message.text, title, when: whenLine(b.scheduledStartAt, tz), zone: zoneName(tz) })
              : message.text}
          </p>
          {action && (!pair || pair.latest[message.bookingId!] === message.id) && (
            <button type="button" onClick={open} className={`${action === 'Join' ? BTN_PRIMARY_SM : BTN_SECONDARY_SM} flex-shrink-0`} data-event-action>
              {action}
            </button>
          )}
        </div>
        {children}
      </div>
    </div>
  );
}
